import React, { useState, useCallback, useMemo } from 'react';
import { Modal, Form } from 'react-bootstrap';
import { useCompanyServiceQueries } from 'services/company';
import { useUserProfileStore } from 'stores/userProfileStore';
import { CreateCompanyRequest } from 'types/company';
import Button from 'components/Button';
import { Loader2 } from 'lucide-react';
import {
  TIN_PLACEHOLDER,
  validateTin,
  formatTinForInput,
} from 'utils/tinUtils';
import { companyVatStatusSelectOptions } from 'utils/companyVatStatusOptions';

const REGISTRATION_OPTIONS = [
  {
    label: 'Sole Proprietorship',
    value: 'sole_proprietorship',
    registrationEntity: 'DTI',
    idPlaceholder: 'DTI Business Name Registration Number',
  },
  {
    label: 'One Person Corporation (OPC)',
    value: 'opc',
    registrationEntity: 'SEC',
    idPlaceholder: 'SEC Registration Number',
  },
  {
    label: 'Corporation (Stock/Non-Stock)',
    value: 'corporation',
    registrationEntity: 'SEC',
    idPlaceholder: 'SEC Registration Number',
  },
  {
    label: 'Partnership (General/Limited)',
    value: 'partnership',
    registrationEntity: 'SEC',
    idPlaceholder: 'SEC Registration Number',
  },
  {
    label: 'Cooperative',
    value: 'cooperative',
    registrationEntity: 'CDA',
    idPlaceholder: 'CDA Registration Number',
  },
] as const;
const DEFAULT_COMPANY_DESCRIPTION = 'No description provided.';

interface CreateCompanyModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
  onError: (message: string) => void;
}

function CreateCompanyModal({
  show,
  onHide,
  onSuccess,
  onError,
}: CreateCompanyModalProps) {
  const currentYear = new Date().getFullYear();
  const defaultFiscalYearStart = `${currentYear}-01-01`;
  const defaultFiscalYearEnd = `${currentYear}-12-31`;
  const companyService = useCompanyServiceQueries();
  const refreshProfile = useUserProfileStore((s) => s.refreshProfile);
  const [companyName, setCompanyName] = useState('');
  const [companyDescription, setCompanyDescription] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [companyTin, setCompanyTin] = useState('');
  const [companyTel, setCompanyTel] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [companyVatStatus, setCompanyVatStatus] = useState('');
  const [entityType, setEntityType] = useState('');
  const [registrationDate, setRegistrationDate] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [tradeName, setTradeName] = useState('');
  const [businessStyle, setBusinessStyle] = useState('');
  const [isBmbe, setIsBmbe] = useState(false);
  const [bmbeRegistrationNumber, setBmbeRegistrationNumber] = useState('');
  const [fiscalYearType, setFiscalYearType] = useState<'calendar' | 'custom'>(
    'calendar',
  );
  const [fiscalYearStart, setFiscalYearStart] = useState(
    defaultFiscalYearStart,
  );
  const [fiscalYearEnd, setFiscalYearEnd] = useState(defaultFiscalYearEnd);
  const [isCreating, setIsCreating] = useState(false);
  const [tinError, setTinError] = useState<string | null>(null);
  const selectedRegistrationOption = REGISTRATION_OPTIONS.find(
    (option) => option.value === entityType,
  );
  const registrationNumberLabel =
    selectedRegistrationOption?.idPlaceholder ||
    'SEC Registration No. or DTI Certificate No.';

  const vatStatusOptions = useMemo(
    () => companyVatStatusSelectOptions(companyVatStatus),
    [companyVatStatus],
  );

  const getTinValidationError = useCallback(validateTin, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!companyName.trim()) {
        onError('Please enter a company name');
        return;
      }

      const tinValidationError = getTinValidationError(companyTin);
      if (tinValidationError) {
        setTinError(tinValidationError);
        return;
      }
      setTinError(null);

      const sanitizedTin = companyTin.replace(/\D/g, '');

      setIsCreating(true);
      try {
        const request: CreateCompanyRequest = {
          name: companyName.trim(),
          description: companyDescription.trim() || DEFAULT_COMPANY_DESCRIPTION,
          ...(companyAddress.trim() && { address: companyAddress.trim() }),
          ...(sanitizedTin && { tin: sanitizedTin }),
          ...(companyTel.trim() && { tel: companyTel.trim() }),
          ...(companyEmail.trim() && { email: companyEmail.trim() }),
          ...(companyVatStatus.trim() && {
            vatStatus: companyVatStatus.trim(),
          }),
          ...(entityType.trim() && { entityType: entityType.trim() }),
          ...(registrationDate && { registrationDate }),
          ...(registrationNumber.trim() && {
            registrationNumber: registrationNumber.trim(),
          }),
          ...(tradeName.trim() && { tradeName: tradeName.trim() }),
          ...(businessStyle.trim() && { businessStyle: businessStyle.trim() }),
          isBmbe,
          ...(isBmbe &&
            bmbeRegistrationNumber.trim() && {
              bmbeRegistrationNumber: bmbeRegistrationNumber.trim(),
            }),
          fiscalYearType,
          ...(fiscalYearStart && { fiscalYearStart }),
          ...(fiscalYearEnd && { fiscalYearEnd }),
        };

        await companyService.createCompany(request);

        // Refresh profile to get updated companies list
        await refreshProfile();

        onSuccess();
        // Reset form
        setCompanyName('');
        setCompanyDescription('');
        setCompanyAddress('');
        setCompanyTin('');
        setCompanyTel('');
        setCompanyEmail('');
        setCompanyVatStatus('');
        setEntityType('');
        setRegistrationDate('');
        setRegistrationNumber('');
        setTradeName('');
        setBusinessStyle('');
        setIsBmbe(false);
        setBmbeRegistrationNumber('');
        setFiscalYearType('calendar');
        setFiscalYearStart(defaultFiscalYearStart);
        setFiscalYearEnd(defaultFiscalYearEnd);
        onHide();
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to create company. Please try again.';
        onError(errorMessage);
      } finally {
        setIsCreating(false);
      }
    },
    [
      companyName,
      companyDescription,
      companyAddress,
      companyTin,
      companyTel,
      companyEmail,
      companyVatStatus,
      entityType,
      registrationDate,
      registrationNumber,
      tradeName,
      businessStyle,
      isBmbe,
      bmbeRegistrationNumber,
      fiscalYearType,
      fiscalYearStart,
      fiscalYearEnd,
      companyService,
      refreshProfile,
      onSuccess,
      onError,
      onHide,
      getTinValidationError,
      defaultFiscalYearStart,
      defaultFiscalYearEnd,
    ],
  );

  const handleClose = useCallback(() => {
    if (!isCreating) {
      setCompanyName('');
      setCompanyDescription('');
      setCompanyAddress('');
      setCompanyTin('');
      setCompanyTel('');
      setCompanyEmail('');
      setCompanyVatStatus('');
      setEntityType('');
      setRegistrationDate('');
      setRegistrationNumber('');
      setTradeName('');
      setBusinessStyle('');
      setIsBmbe(false);
      setBmbeRegistrationNumber('');
      setFiscalYearType('calendar');
      setFiscalYearStart(defaultFiscalYearStart);
      setFiscalYearEnd(defaultFiscalYearEnd);
      setTinError(null);
      onHide();
    }
  }, [defaultFiscalYearEnd, defaultFiscalYearStart, isCreating, onHide]);

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Create New Company</Modal.Title>
      </Modal.Header>
      <Form onSubmit={(e) => void handleSubmit(e)}>
        <Modal.Body>
          <Form.Group controlId="companyName" className="mb-3">
            <Form.Label>
              Company Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Enter company name"
              disabled={isCreating}
              required
            />
          </Form.Group>

          <Form.Group controlId="companyDescription" className="mb-3">
            <Form.Label>
              Company Description <span className="text-muted">(Optional)</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={companyDescription}
              onChange={(e) => setCompanyDescription(e.target.value)}
              placeholder="Enter company description"
              disabled={isCreating}
            />
          </Form.Group>

          <Form.Group controlId="companyAddress" className="mb-3">
            <Form.Label>
              Address <span className="text-muted">(Optional)</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={companyAddress}
              onChange={(e) => setCompanyAddress(e.target.value)}
              placeholder="Enter company address"
              disabled={isCreating}
            />
          </Form.Group>

          <Form.Group controlId="companyTin" className="mb-3">
            <Form.Label>
              Tax Identification Number (TIN){' '}
              <span className="text-muted">(Optional)</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={companyTin}
              onChange={(e) => {
                setCompanyTin(e.target.value);
                if (tinError) setTinError(null);
              }}
              onBlur={() => {
                const error = getTinValidationError(companyTin);
                if (error) {
                  setTinError(error);
                  return;
                }
                const formatted = formatTinForInput(companyTin);
                setCompanyTin(formatted);
              }}
              placeholder={TIN_PLACEHOLDER}
              disabled={isCreating}
            />
            {tinError && (
              <Form.Text className="text-danger">{tinError}</Form.Text>
            )}
          </Form.Group>

          <Form.Group controlId="companyTel" className="mb-3">
            <Form.Label>
              Tel <span className="text-muted">(Optional)</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={companyTel}
              onChange={(e) => setCompanyTel(e.target.value)}
              placeholder="Enter telephone number"
              disabled={isCreating}
            />
          </Form.Group>

          <Form.Group controlId="companyEmail" className="mb-3">
            <Form.Label>
              Email <span className="text-muted">(Optional)</span>
            </Form.Label>
            <Form.Control
              type="email"
              value={companyEmail}
              onChange={(e) => setCompanyEmail(e.target.value)}
              placeholder="Enter company email"
              disabled={isCreating}
            />
          </Form.Group>

          <Form.Group controlId="companyVatStatus" className="mb-3">
            <Form.Label>
              VAT Status <span className="text-muted">(Optional)</span>
            </Form.Label>
            <Form.Select
              value={companyVatStatus}
              onChange={(e) => setCompanyVatStatus(e.target.value)}
              disabled={isCreating}
            >
              {vatStatusOptions.map((opt) => (
                <option key={opt.value || '__empty'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group controlId="entityType" className="mb-3">
            <Form.Label>Type of Entity</Form.Label>
            <Form.Select
              value={entityType}
              onChange={(e) => setEntityType(e.target.value)}
              disabled={isCreating}
            >
              <option value="">Select type of entity</option>
              {REGISTRATION_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group controlId="registrationDate" className="mb-3">
            <Form.Label>Date of Registration</Form.Label>
            <Form.Control
              type="date"
              value={registrationDate}
              onChange={(e) => setRegistrationDate(e.target.value)}
              disabled={isCreating}
            />
          </Form.Group>

          <Form.Group controlId="registrationNumber" className="mb-3">
            <Form.Label>{registrationNumberLabel}</Form.Label>
            <Form.Control
              type="text"
              value={registrationNumber}
              onChange={(e) => setRegistrationNumber(e.target.value)}
              placeholder={registrationNumberLabel}
              disabled={isCreating}
            />
          </Form.Group>

          <Form.Group controlId="tradeName" className="mb-3">
            <Form.Label>Trade Name</Form.Label>
            <Form.Control
              type="text"
              value={tradeName}
              onChange={(e) => setTradeName(e.target.value)}
              placeholder="e.g., Operating under the trade/business name of..."
              disabled={isCreating}
            />
          </Form.Group>

          <Form.Group controlId="businessStyle" className="mb-3">
            <Form.Label>Business Style</Form.Label>
            <Form.Control
              type="text"
              value={businessStyle}
              onChange={(e) => setBusinessStyle(e.target.value)}
              placeholder="e.g., Laundry Services, Transportation, I.T. Services"
              disabled={isCreating}
            />
          </Form.Group>

          <Form.Group controlId="isBmbe" className="mb-3">
            <Form.Check
              type="switch"
              label="Registered as BMBE"
              checked={isBmbe}
              onChange={(e) => {
                setIsBmbe(e.target.checked);
                if (!e.target.checked) {
                  setBmbeRegistrationNumber('');
                }
              }}
              disabled={isCreating}
            />
          </Form.Group>

          {isBmbe && (
            <Form.Group controlId="bmbeRegistrationNumber" className="mb-3">
              <Form.Label>BMBE Registration No.</Form.Label>
              <Form.Control
                type="text"
                value={bmbeRegistrationNumber}
                onChange={(e) => setBmbeRegistrationNumber(e.target.value)}
                placeholder="Enter BMBE registration number"
                disabled={isCreating}
              />
            </Form.Group>
          )}

          <Form.Group controlId="fiscalYearType" className="mb-3">
            <Form.Label>Fiscal Year</Form.Label>
            <Form.Select
              value={fiscalYearType}
              onChange={(e) =>
                setFiscalYearType(e.target.value as 'calendar' | 'custom')
              }
              disabled={isCreating}
            >
              <option value="calendar">Calendar Year</option>
              <option value="custom">Custom Fiscal Year</option>
            </Form.Select>
          </Form.Group>

          <Form.Group controlId="fiscalYearStart" className="mb-3">
            <Form.Label>Fiscal Year Start Date</Form.Label>
            <Form.Control
              type="date"
              value={fiscalYearStart}
              onChange={(e) => setFiscalYearStart(e.target.value)}
              disabled={isCreating || fiscalYearType === 'calendar'}
            />
          </Form.Group>

          <Form.Group controlId="fiscalYearEnd" className="mb-3">
            <Form.Label>Fiscal Year End Date</Form.Label>
            <Form.Control
              type="date"
              value={fiscalYearEnd}
              onChange={(e) => setFiscalYearEnd(e.target.value)}
              disabled={isCreating || fiscalYearType === 'calendar'}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isCreating || !companyName.trim()}>
            {isCreating ? (
              <>
                <Loader2
                  size={16}
                  className="me-2"
                  style={{ animation: 'spin 1s linear infinite' }}
                />
                Creating...
              </>
            ) : (
              'Create Company'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default CreateCompanyModal;
